import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Hammer, Home, Handshake, RotateCw, Menu, X, Building2, Briefcase, Clock, Phone, TrendingUp, ClipboardList, Wrench } from "lucide-react";
import Gallery from "./Gallery";

import AOS from "aos";
import "aos/dist/aos.css";

// Version 3.0 - Multi-Target Group Support

export default function HomePage() {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Bestimme customerType aus der URL
  const getCustomerTypeFromUrl = () => {
    if (location.pathname === "/privatkunden") {
      return "private";
    } else if (location.pathname === "/geschaeftskunden") {
      return "business";
    }
    return null;
  };
  
  const [customerType, setCustomerType] = useState(() => {
    // Versuche zuerst aus URL zu lesen
    const urlType = getCustomerTypeFromUrl();
    if (urlType) {
      // Speichere auch in localStorage für Konsistenz
      localStorage.setItem("customerType", urlType);
      return urlType;
    }
    // Fallback: aus localStorage
    return localStorage.getItem("customerType");
  });
  
  useEffect(() => {
    // AOS initialisieren mit Refresh-Option
    AOS.init({ 
      duration: 1000,
      once: false, // Animationen können mehrfach ausgelöst werden
      mirror: true // Animationen auch beim Rückscrollen
    });
    
    // AOS nach kurzer Verzögerung refreshen, damit alle Elemente erkannt werden
    setTimeout(() => {
      AOS.refresh();
    }, 100);
  }, []);

  const [showCookie, setShowCookie] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    telefonnummer: "",
    adresse: "",
    art: "",
    baujahr: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [submitSuccess, setSubmitSuccess] = useState(false);

  useEffect(() => {
    // Prüfe customerType aus URL
    const urlType = getCustomerTypeFromUrl();
    
    if (urlType) {
      // URL enthält customerType - verwende diesen
      setCustomerType(urlType);
      localStorage.setItem("customerType", urlType);
    } else {
      // Kein customerType in URL - weiterleiten zur Auswahlseite
      navigate("/", { replace: true });
      return;
    }

    const accepted = localStorage.getItem("cookieAccepted");
    if (!accepted) setShowCookie(true);
  }, [navigate, location.pathname]);

  const handleCustomerTypeSelect = (type) => {
    // Diese Funktion wird jetzt nur noch für Wechsel verwendet
    setCustomerType(type);
    localStorage.setItem("customerType", type);
    // Navigiere zur entsprechenden URL
    if (type === "private") {
      navigate("/privatkunden", { replace: true });
    } else if (type === "business") {
      navigate("/geschaeftskunden", { replace: true });
    }
    // Scroll nach oben
    window.scrollTo(0, 0);
  };

  const acceptCookies = () => {
    localStorage.setItem("cookieAccepted", "true");
    setShowCookie(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setSubmitError("");
  };

  const handleSubmit = async (e) => {
    // ABSOLUTER GUARD: Verhindere jeglichen normalen Submit
    if (e && typeof e.preventDefault === 'function') {
      e.preventDefault();
    }
    if (e && typeof e.stopPropagation === 'function') {
      e.stopPropagation();
    }
    if (e && typeof e.stopImmediatePropagation === 'function') {
      e.stopImmediatePropagation();
    }
    
    // Zusätzlicher Schutz: Entferne action falls vorhanden
    if (e && e.target && e.target.action) {
      e.target.action = '';
    }
    
    setIsSubmitting(true);
    setSubmitError("");
    setSubmitSuccess(false);

    try {
      // Erstelle FormData für application/x-www-form-urlencoded
      const formDataToSend = new URLSearchParams();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('telefonnummer', formData.telefonnummer);
      formDataToSend.append('adresse', formData.adresse || '');
      formDataToSend.append('art', formData.art || '');
      formDataToSend.append('baujahr', formData.baujahr || '');
      formDataToSend.append('message', formData.message);

      const response = await fetch('/send-email.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formDataToSend.toString()
      });

      const result = await response.json();

      if (result.ok === true) {
        setSubmitSuccess(true);
        // Formular zurücksetzen
        setFormData({
          name: "",
          email: "",
          telefonnummer: "",
          adresse: "",
          art: "",
          baujahr: "",
          message: ""
        });
        setIsSubmitting(false);
        
        // Grünes Feld kurz anzeigen (2.5 Sekunden), dann nach oben scrollen
        setTimeout(() => {
          // Scroll nach oben zur Startseite
          window.scrollTo({ top: 0, behavior: 'smooth' });
          // Nach dem Scrollen Erfolgsmeldung ausblenden
          setTimeout(() => {
            setSubmitSuccess(false);
          }, 500);
        }, 2500); // 2.5 Sekunden warten, damit das grüne Feld lesbar ist
        
        // AOS refreshen
        setTimeout(() => {
          AOS.refresh();
        }, 100);
      } else {
        throw new Error(result.error || 'Fehler beim Senden der E-Mail');
      }
    } catch (error) {
      console.error("Fehler beim Senden der E-Mail:", error);
      setSubmitError(error.message || "Es ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut oder kontaktieren Sie uns direkt per E-Mail.");
      setIsSubmitting(false);
    }
  };

  // Wenn kein customerType, zeige nichts (wird zur Auswahlseite weitergeleitet)
  if (!customerType) {
    return null;
  }

  return (
    <div className="min-h-screen bg-white text-black font-sans">
      {/* Hero-Bereich mit Hintergrundbild */}
    
<section
  className={`relative bg-cover bg-center ${!customerType ? 'h-screen' : 'min-h-[100dvh]'}`}
  style={{ backgroundImage: "url('/hero3.jpg')" }}
>
  {/* Overlay */}
  <div className="absolute inset-0 bg-black bg-opacity-50 text-white text-center px-4">
  {/* Logo ganz oben - fixed height container to prevent layout shift */}
  <div className="flex flex-col items-center pt-6 relative" style={{ minHeight: '180px' }}>
    <button
      onClick={() => {
        // Lösche die Auswahl und gehe zur Auswahlseite
        localStorage.removeItem("customerType");
        navigate("/", { replace: true });
      }}
      className="cursor-pointer hover:opacity-80 transition-opacity"
      aria-label="Zur Auswahlseite"
    >
      <img src="/imenso-logo-trans.png" alt="IMENSO Logo" className="h-24 md:h-32" />
    </button>
    
    {/* Burger Menu */}
    <button
      onClick={() => setMenuOpen(!menuOpen)}
      className="mt-4 p-2 rounded-md hover:bg-white hover:bg-opacity-20 transition-colors"
      aria-label="Menu"
    >
      {menuOpen ? (
        <X className="w-6 h-6 text-white" />
      ) : (
        <Menu className="w-6 h-6 text-white" />
      )}
    </button>
    
    {/* Dropdown Menu - absolutely positioned to prevent layout shift */}
    {menuOpen && (
      <div className="absolute top-full mt-2 left-1/2 transform -translate-x-1/2 bg-transparent rounded-lg shadow-lg min-w-[200px] z-50">
        <nav className="flex flex-col py-2">
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              if (!customerType) {
                // Erste Auswahl - setze Privatkunde
                handleCustomerTypeSelect("private");
              } else {
                // Wechsel zu Privatkunde
                handleCustomerTypeSelect("private");
              }
              setMenuOpen(false);
            }}
            className="px-6 py-3 text-white hover:bg-white hover:bg-opacity-20 transition-colors"
          >
            Privatkunden
          </a>
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              if (!customerType) {
                // Erste Auswahl - setze Geschäftskunde
                handleCustomerTypeSelect("business");
              } else {
                // Wechsel zu Geschäftskunde
                handleCustomerTypeSelect("business");
              }
              setMenuOpen(false);
            }}
            className="px-6 py-3 text-white hover:bg-white hover:bg-opacity-20 transition-colors"
          >
            Geschäftskunden
          </a>
        </nav>
      </div>
    )}
  </div>

  {/* Texte zentriert in der Mitte - unterschiedlich je nach Kundentyp */}
  <div className="flex flex-col items-center justify-center min-h-[calc(100dvh-6rem)] mt-4">
    {customerType ? (
      <>
        <h1 className="text-3xl md:text-5xl font-semibold mb-4">
          {customerType === "private" ? (
            <>Immobilienentwicklung <br /> mit Substanz</>
          ) : (
            <>Ihr Partner für <br /> Immobilienaufwertung</>
          )}
        </h1>
        <p className="text-xl md:text-2xl text-gray-200">Erding · Freising · Landshut · München · Regensburg</p>
      </>
    ) : (
      <>
        <h1 className="text-3xl md:text-5xl font-semibold mb-4">Willkommen bei IMENSO</h1>
        <p className="text-xl md:text-2xl text-gray-200">Bitte wählen Sie im Menü oben</p>
      </>
    )}
  </div>
</div>

{/* Button-Leiste am rechten Rand im Hero-Bereich */}
<div className="fixed top-[28%] right-4 z-50 flex flex-col gap-4">
  {/* E-Mail */}
  <a
    href="#kontakt"
    className="bg-gray-700 hover:bg-gray-800 text-white p-3 rounded-full shadow-lg transition"
    title="Kontaktformular öffnen"
  >
    <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
      <path d="M2.25 6.75A2.25 2.25 0 014.5 4.5h15a2.25 2.25 0 012.25 2.25v10.5A2.25 2.25 0 0119.5 19.5h-15A2.25 2.25 0 012.25 17.25V6.75zm1.77-.75l7.98 5.04a.75.75 0 00.8 0l7.98-5.04H4.02z" />
    </svg>
  </a>

  {/* Telefon */}
  <a
    href="tel:+491717331116"
    className="bg-blue-600 hover:bg-blue-700 text-white p-3 rounded-full shadow-lg transition"
    title="Jetzt anrufen"
  >
    <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
      <path d="M6.62 10.79a15.05 15.05 0 006.59 6.59l2.2-2.2a1 1 0 011.05-.24 11.36 11.36 0 003.58.57 1 1 0 011 1V20a1 1 0 01-1 1A17 17 0 013 4a1 1 0 011-1h3.5a1 1 0 011 1 11.36 11.36 0 00.57 3.58 1 1 0 01-.24 1.05l-2.2 2.2z" />
    </svg>
  </a>

  {/* WhatsApp */}
  <a
    href="https://wa.me/491717331116"
    target="_blank"
    rel="noopener noreferrer"
    className="bg-green-500 hover:bg-green-600 text-white p-3 rounded-full shadow-lg transition"
    title="WhatsApp"
  >
    <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
      <path d="M12.04 2.003a9.96 9.96 0 00-8.204 15.569L2 22l4.516-1.818A9.96 9.96 0 1012.04 2.003zm0 2.017a7.94 7.94 0 110 15.88 7.94 7.94 0 010-15.88zm3.942 11.364c-.186.521-.957.973-1.316.997-.359.025-.798.177-2.71-.568-2.295-.889-3.774-3.289-3.889-3.446-.114-.156-.928-1.24-.928-2.36s.586-1.686.793-1.92c.207-.234.454-.293.607-.293.153 0 .307.002.441.008.142.007.332-.054.521.39.187.443.635 1.528.693 1.637.059.11.098.234.02.39-.078.155-.116.253-.234.39-.117.137-.247.307-.353.412-.117.117-.24.24-.105.475.134.234.596.981 1.28 1.589.886.797 1.648 1.048 1.883 1.162.234.115.367.098.504-.059.137-.156.583-.676.74-.907.156-.23.312-.19.53-.117.219.073 1.37.648 1.604.765.234.118.39.175.448.273.06.098.06.564-.127 1.086z" />
    </svg>
  </a>
</div>



 <svg
  className="absolute bottom-0 left-0 w-full h-[40px] block"
  viewBox="0 0 100 10"
  preserveAspectRatio="none"
>
  <polygon fill="#f3f4f6" points="0,10 100,0 100,10" />

</svg>

</section>

{/* Rest der Seite nur anzeigen, wenn Kundentyp gewählt wurde */}
{customerType && (
  <>
{/* Unterschiedliche Inhalte je nach Kundentyp */}
{customerType === "private" ? (
  <section className="bg-gray-100 py-16 px-6 w-full" data-aos="fade-up">
    <h2 className="text-3xl font-bold mb-6 text-center">Kennen Sie das?</h2>
    <div className="max-w-3xl mx-auto text-center space-y-6 text-lg leading-relaxed text-gray-700">
      <p className="text-lg leading-relaxed text-gray-700 text-center">
        Sie besitzen eine Immobilie, vielleicht schon seit vielen Jahren. Sie wissen, dass sie Aufmerksamkeit braucht – aber der Aufwand wirkt erdrückend. Zeit, Energie und die Suche nach verlässlichen Handwerkern rauben Ihnen den Schlaf.
      </p>
      <p className="text-lg leading-relaxed text-gray-700 text-center mt-4">
        <strong>Vielleicht denken Sie über einen Verkauf nach.</strong> Sie haben geerbt oder möchten sich verändern, suchen aber jemanden, der das Haus mit Respekt behandelt. Jemanden, der nicht nur „renoviert", sondern neu belebt und den Wert Ihrer Immobilie wirklich schätzt. Wir kaufen Ihre Immobilie direkt, fair und ohne Umwege – damit Sie die Verantwortung in gute Hände abgeben können.
      </p>
      <p className="text-lg leading-relaxed text-gray-700 text-center mt-4">
        <strong>Vielleicht möchten Sie aber auch bleiben.</strong> Sie lieben Ihr Zuhause, doch die Substanz ist in die Jahre gekommen. Sie wünschen sich eine Modernisierung, die den Charakter bewahrt und gleichzeitig den Wohnwert steigert – ohne dass Sie sich um die Koordination der Baustelle kümmern müssen. Wir renovieren für Sie auf höchstem Niveau, als wäre es unser eigenes Objekt.
      </p>
      <p className="font-bold text-center mt-6 mb-6">
        Genau hier kommen wir ins Spiel.
      </p>
      <p className="text-lg leading-relaxed text-gray-700 text-center mb-16">
        Wir sind Ihr Partner für Immobilien mit Geschichte. Ob wir Ihr Objekt kaufen oder für Sie sanieren: Wir geben Ihrem Zuhause eine Zukunft. Behutsam, kompetent und mit Substanz.
      </p>

      <div className="flex flex-col md:flex-row items-center justify-center gap-8 mt-8">
        <div className="flex flex-col items-center text-center max-w-xs">
          <div className="bg-green-100 rounded-full p-4 mb-4">
            <Clock className="w-12 h-12 text-green-600" />
          </div>
          <h3 className="font-bold text-lg mb-2">Schnelligkeit</h3>
          <p className="text-lg leading-relaxed text-gray-700">
            Wir bringen Leerstand in Rekordzeit zurück in die Vermietung.
          </p>
        </div>
        
        <div className="flex flex-col items-center text-center max-w-xs">
          <div className="bg-green-100 rounded-full p-4 mb-4">
            <Phone className="w-12 h-12 text-green-600" />
          </div>
          <h3 className="font-bold text-lg mb-2">Erreichbarkeit</h3>
          <p className="text-lg leading-relaxed text-gray-700">
            Ein Ansprechpartner, klare Kommunikation, kein Hinterherlaufen.
          </p>
        </div>
        
        <div className="flex flex-col items-center text-center max-w-xs">
          <div className="bg-green-100 rounded-full p-4 mb-4">
            <TrendingUp className="w-12 h-12 text-green-600" />
          </div>
          <h3 className="font-bold text-lg mb-2">Wertsteigerung</h3>
          <p className="text-lg leading-relaxed text-gray-700">
            Wir sanieren nicht nur, wir optimieren den Marktwert Ihrer Einheiten.
          </p>
        </div>
      </div>
    </div>
  </section>
) : (
  <section className="bg-gray-100 py-16 px-6 w-full" data-aos="fade-up">
    <h2 className="text-3xl font-bold mb-6 text-center">Kennen Sie das?</h2>
    <div className="max-w-3xl mx-auto text-center space-y-6 text-lg leading-relaxed text-gray-700">
      <p className="text-lg leading-relaxed text-gray-700 text-center">
        Sie betreuen ein Objekt, das Potenzial hat, aber der aktuelle Zustand blockiert alles.
      </p>
      
      <p className="text-lg leading-relaxed text-gray-700 text-center">
        <strong>Für Sie als Hausverwaltung</strong> bedeutet Sanierungsstau vor allem eines: Ärger. Mietausfall, langwierige Abstimmungen mit unzuverlässigen Gewerken und ständig neue Baustellen, die Ihnen die Zeit für das Wesentliche rauben. Sie brauchen keinen weiteren Dienstleister – Sie brauchen Ruhe im Bestand.
      </p>

      <p className="text-lg leading-relaxed text-gray-700 text-center">
        <strong>Für Sie als Makler</strong> ist eine renovierungsbedürftige Immobilie oft eine Bremse im Vertrieb. Die Vermarktungsdauer zieht sich, und der erzielbare Preis bleibt unter den Möglichkeiten. Sie brauchen einen Partner, der das Objekt marktreif macht – schnell, kalkulierbar und mit dem Auge für das, was Käufer suchen.
      </p>

      <p className="font-bold text-center mt-6 mb-6">
        Genau hier kommen wir ins Spiel.
      </p>

      <p className="text-lg leading-relaxed text-gray-700 text-center mb-16">
        Wir sind Ihr verlängerter Arm für die Immobilienaufwertung.
      </p>

      <div className="flex flex-col md:flex-row items-center justify-center gap-8 mt-8">
        <div className="flex flex-col items-center text-center max-w-xs">
          <div className="bg-green-100 rounded-full p-4 mb-4">
            <Clock className="w-12 h-12 text-green-600" />
          </div>
          <h3 className="font-bold text-lg mb-2">Schnelligkeit</h3>
          <p className="text-lg leading-relaxed text-gray-700">
            Wir bringen Leerstand in Rekordzeit zurück in die Vermietung.
          </p>
        </div>
        
        <div className="flex flex-col items-center text-center max-w-xs">
          <div className="bg-green-100 rounded-full p-4 mb-4">
            <Phone className="w-12 h-12 text-green-600" />
          </div>
          <h3 className="font-bold text-lg mb-2">Erreichbarkeit</h3>
          <p className="text-lg leading-relaxed text-gray-700">
            Ein Ansprechpartner, klare Kommunikation, kein Hinterherlaufen.
          </p>
        </div>
        
        <div className="flex flex-col items-center text-center max-w-xs">
          <div className="bg-green-100 rounded-full p-4 mb-4">
            <TrendingUp className="w-12 h-12 text-green-600" />
          </div>
          <h3 className="font-bold text-lg mb-2">Wertsteigerung</h3>
          <p className="text-lg leading-relaxed text-gray-700">
            Wir sanieren nicht nur, wir optimieren den Marktwert Ihrer Einheiten.
          </p>
        </div>
      </div>
    </div>
  </section>
)}

{/* Galerie - für beide Kundentypen, zwischen "Kennen Sie das?" und "Über uns" */}
{/* TODO: Galerie später aktivieren, wenn ausreichend Bilder vorhanden sind */}
{/* <Gallery /> */}

<section className="bg-gray-100 py-16 px-6 w-full" data-aos="fade-up">
        <h2 className="text-3xl font-bold mb-6 text-center">Über uns</h2>
          <div className="max-w-3xl mx-auto text-center space-y-6 text-lg leading-relaxed text-gray-700">
        <p className="text-lg leading-relaxed text-gray-700 text-center">
          Wir sind IMENSO – ein regionales Unternehmen aus Landshut in Niederbayern mit Fokus auf den Ankauf,
          die Sanierung und die Aufwertung von Wohnimmobilien. Unser Anspruch ist es,<br />
          Gebäude mit Geschichte und Potenzial neu zum Leben zu erwecken. Dabei arbeiten wir
          respektvoll, transparent und mit Herz.
        </p>

        <div className="mt-10 flex flex-col md:flex-row items-center justify-center gap-8">
  <img src="/anja.jpg" alt="Teammitglied 1" className="w-24 h-24 sm:w-32 sm:h-32 md:w-40 md:h-40 rounded-full object-cover shadow-md" />
  <img src="/jan.jpg" alt="Teammitglied 2" className="w-24 h-24 sm:w-32 sm:h-32 md:w-40 md:h-40 rounded-full object-cover shadow-md" />
  <img src="/benjamin.jpg" alt="Teammitglied 3" className="w-24 h-24 sm:w-32 sm:h-32 md:w-40 md:h-40 rounded-full object-cover shadow-md" />
</div>
</div>
      </section>


<section id="leistungen" className="bg-gray-100 py-16 px-6 w-full" data-aos="fade-up">
  <div className="max-w-3xl mx-auto">
    <h2 className="text-3xl font-bold mb-10 text-center">Unsere Leistungen</h2>
  </div>

  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto">
    {customerType === "private" ? (
      <>
        <div className="border border-green-600 rounded-xl p-6 shadow-md bg-white text-center">
          <Hammer className="w-8 h-8 text-green-600 mx-auto mb-4" />
          <h3 className="font-bold text-lg mb-2">Sanierung&nbsp;&amp;&nbsp;Renovierung</h3>
          <p>Blick fürs Detail, Qualität im Handwerk.</p>
        </div>
        <div className="border border-green-600 rounded-xl p-6 shadow-md bg-white text-center">
          <Handshake className="w-8 h-8 text-green-600 mx-auto mb-4" />
          <h3 className="font-bold text-lg mb-2">Faire Abwicklung</h3>
          <p>Transparent, respektvoll, zuverlässig.</p>
        </div>
        <div className="border border-green-600 rounded-xl p-6 shadow-md bg-white text-center">
          <Home className="w-8 h-8 text-green-600 mx-auto mb-4" />
          <h3 className="font-bold text-lg mb-2">Direktankauf</h3>
          <p>Ohne Makler, schnell und unkompliziert.</p>
        </div>
        <div className="border border-green-600 rounded-xl p-6 shadow-md bg-white text-center">
          <RotateCw className="w-8 h-8 text-green-600 mx-auto mb-4" />
          <h3 className="font-bold text-lg mb-2">Wiederverkauf</h3>
          <p>Neuer Wohnwert mit Charakter.</p>
        </div>
      </>
    ) : (
      <>
        <div className="border border-green-600 rounded-xl p-6 shadow-md bg-white text-center">
          <Wrench className="w-8 h-8 text-green-600 mx-auto mb-4" />
          <h3 className="font-bold text-lg mb-2">Leerstandssanierung</h3>
          <p>Schnelle Wiederinbetriebnahme leerstehender Objekte.</p>
        </div>
        <div className="border border-green-600 rounded-xl p-6 shadow-md bg-white text-center">
          <Briefcase className="w-8 h-8 text-green-600 mx-auto mb-4" />
          <h3 className="font-bold text-lg mb-2">Beratung & Planung</h3>
          <p>Maßgeschneiderte Konzepte für Ihre Bedürfnisse.</p>
        </div>
        <div className="border border-green-600 rounded-xl p-6 shadow-md bg-white text-center">
          <Hammer className="w-8 h-8 text-green-600 mx-auto mb-4" />
          <h3 className="font-bold text-lg mb-2">Sanierung & Modernisierung</h3>
          <p>Professionelle Umsetzung Ihrer Projekte.</p>
        </div>
        <div className="border border-green-600 rounded-xl p-6 shadow-md bg-white text-center">
          <ClipboardList className="w-8 h-8 text-green-600 mx-auto mb-4" />
          <h3 className="font-bold text-lg mb-2">Projektmanagement</h3>
          <p>Professionelle Koordination von A bis Z.</p>
        </div>
      </>
    )}
  </div>
</section>

<section id="kontakt" className="bg-gray-100 py-16 px-6 w-full" data-aos="fade-up">
        <div className="max-w-2xl mx-auto">
          <h2 className="text-3xl font-bold mb-6 text-center">Kontakt</h2>
          <p className="mb-1 text-lg text-center text-gray-700">
            Schreiben Sie uns.
          </p>
          <p className="mb-6 text-lg text-center text-gray-700">
            Wir freuen uns auf Ihre Nachricht.
          </p>

          <form
            onSubmit={handleSubmit}
            action=""
            method="post"
            className="grid grid-cols-1 gap-4"
            noValidate
            id="contact-form"
          >
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder="Ihr Name"
              required
              className="p-3 border border-gray-300 rounded-md"
              disabled={isSubmitting}
            />

            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              placeholder="Ihre E-Mail"
              required
              className="p-3 border border-gray-300 rounded-md"
              disabled={isSubmitting}
            />

            <input
              type="tel"
              name="telefonnummer"
              value={formData.telefonnummer}
              onChange={handleInputChange}
              placeholder="Ihre Telefonnummer"
              required
              className="p-3 border border-gray-300 rounded-md"
              disabled={isSubmitting}
            />

            <input
              type="text"
              name="adresse"
              value={formData.adresse}
              onChange={handleInputChange}
              placeholder="Optional: Adresse Ihrer Immobilie"
              className="p-3 border border-gray-300 rounded-md"
              disabled={isSubmitting}
            />

            <input
              type="text"
              name="art"
              value={formData.art}
              onChange={handleInputChange}
              placeholder="Optional: Art Ihrer Immobilie (z. B. Einfamilienhaus, Wohnung)"
              className="p-3 border border-gray-300 rounded-md"
              disabled={isSubmitting}
            />

            <input
              type="text"
              name="baujahr"
              value={formData.baujahr}
              onChange={handleInputChange}
              placeholder="Optional: Baujahr Ihrer Immobilie"
              className="p-3 border border-gray-300 rounded-md"
              disabled={isSubmitting}
            />

            <textarea
              name="message"
              value={formData.message}
              onChange={handleInputChange}
              placeholder="Ihre Nachricht"
              rows="5"
              required
              className="p-3 border border-gray-300 rounded-md"
              disabled={isSubmitting}
            ></textarea>

            {submitError && (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
                {submitError}
              </div>
            )}

            {submitSuccess && (
              <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded">
                Vielen Dank für Ihre Nachricht! Wir melden uns schnellstmöglich bei Ihnen.
              </div>
            )}

            <button
              type="submit"
              disabled={isSubmitting}
              className="bg-black hover:bg-gray-800 disabled:bg-gray-400 disabled:cursor-not-allowed text-white px-6 py-3 rounded-md transition"
            >
              {isSubmitting ? "Wird gesendet..." : "Nachricht senden"}
            </button>
          </form>
        </div>
      </section>


<section id="impressum" className="bg-white text-gray-700 py-16 px-6 max-w-6xl mx-auto">
  <h2 className="text-2xl font-bold mb-6">Impressum</h2>
  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
    <div>
      <p className="mb-3">
        <strong>IMENSO GmbH</strong><br />
        Am Bahndamm 6<br />
        84100 Niederaichbach
      </p>
      <p className="mb-3">
        Telefon: 0171 733 11 16<br />
        E-Mail: info@imenso.net
      </p>
    </div>
    <div>
      <p className="mb-3">
        Vertretungsberechtigter Geschäftsführer:<br />
        Benjamin Sonderwald
      </p>
      <p className="mb-3">
        Registergericht:<br />
        Amtsgericht Landshut
      </p>
      <p className="mb-3">
        Registernummer:<br />
        HRB 14839
      </p>
    </div>
  </div>
</section>


<footer className="text-center text-sm text-gray-500 py-6 border-t">
  &copy; {new Date().getFullYear()} IMENSO GmbH. Alle Rechte vorbehalten.
<a href="/datenschutz" className="ml-2 hover:underline">Datenschutzerklärung</a>
</footer>
  </>
)}

{/* Cookie Banner - immer sichtbar */}
{showCookie && (
  <div className="fixed bottom-0 left-0 right-0 bg-gray-800 text-white p-4 z-50 text-sm flex flex-col md:flex-row items-center justify-between">
    <p className="mb-2 md:mb-0">
      Diese Website verwendet Cookies, um Ihnen ein optimales Erlebnis zu bieten.{" "}
      <a href="#datenschutz" className="underline">Mehr erfahren</a>
    </p>
    <button
      onClick={acceptCookies}
      className="bg-white text-black px-4 py-2 rounded hover:bg-gray-100"
    >
      Verstanden
    </button>
  </div>
)}


    </div>
  );
}

